import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { useAuth } from '@/app/core/auth/AuthProvider'
import { canAccessModule, normalizeRoles } from '@/app/core/auth/useRoles'
import type {
  AdminUser,
  ApprovalThreshold,
  AuditLog,
  CustomField,
  Integration,
  LegalEntity,
  Role,
  Tenant,
  UserInvitation,
} from '@/data/admin/types'

/* ── Fragments ── */

const TENANT_FIELDS = gql`
  fragment TenantFields on Tenant {
    id
    name
    code
    subdomain
    status
    dataResidency
    settings {
      multiCurrencyEnabled
      defaultCurrency
      timezone
      invoiceNumberingFormat
      enableNotifications
      customSettings { key value }
    }
  }
`

const USER_FIELDS = gql`
  fragment UserFields on User {
    id
    email
    displayName
    roles
    status
    preferences {
      language
      dateFormat
      timeFormat
      customPreferences { key value }
    }
    legalEntity { id name }
    lastLoginAt
  }
`

const LEGAL_ENTITY_FIELDS = gql`
  fragment LegalEntityFields on LegalEntity {
    id
    name
    registrationNumber
    defaultCurrency
    isPrimary
    address { line1 line2 city state postalCode country street }
    defaultTaxProfile { type taxId }
    branches {
      id
      name
      code
      address { line1 line2 city state postalCode country street }
    }
  }
`

const INTEGRATION_FIELDS = gql`
  fragment IntegrationFields on Integration {
    id
    type
    name
    status
    lastSyncAt
    lastSyncStatus
    lastSyncError
    syncConfig {
      syncFrequency
      conflictResolutionRule
      sourceOfTruth
      syncEnabled
    }
  }
`

const AUDIT_FIELDS = gql`
  fragment AuditFields on AuditLog {
    id
    entityType
    entityId
    action
    actorId
    actorName
    actorEmail
    timestamp
    previousState
    newState
    beforeValue
    afterValue
    ipAddress
  }
`

const CUSTOM_FIELD_FIELDS = gql`
  fragment CustomFieldFields on AdminCustomField {
    id
    entityType
    fieldKey
    label
    fieldType
    required
    options
    active
    createdAt
  }
`

const INVITATION_FIELDS = gql`
  fragment InvitationFields on UserInvitation {
    id
    email
    roles
    legalEntityId
    status
    expiresAt
    invitedByEmail
    createdAt
    acceptedAt
  }
`

const APPROVAL_THRESHOLD_FIELDS = gql`
  fragment ApprovalThresholdFields on ApprovalThreshold {
    id
    entityType
    minAmount
    maxAmount
    currency
    requiredRole
    active
    createdAt
  }
`

/* ── Queries ── */

// Always runs (every authenticated user): powers Profile/preferences and role display.
const GET_CURRENT_USER = gql`
  query AdmCurrentUser {
    currentUser { ...UserFields }
  }
  ${USER_FIELDS}
`

// Admin-only overview. The sub-fields are @RequireRole({ADMIN,AUDITOR}) on the backend,
// so this is skipped for users without admin access to avoid per-session authorization
// errors and needless queries.
const GET_ADMIN = gql`
  query AdmOverview {
    currentTenant { ...TenantFields }
    users(pagination: { page: 0, size: 200 }) {
      users { ...UserFields }
      totalCount
    }
    legalEntities { ...LegalEntityFields }
    integrations { ...IntegrationFields }
    customFields { ...CustomFieldFields }
    approvalThresholds { ...ApprovalThresholdFields }
    invitations { ...InvitationFields }
    auditLogs(pagination: { page: 0, size: 200 }) {
      auditLogs { ...AuditFields }
      totalCount
    }
  }
  ${TENANT_FIELDS}
  ${USER_FIELDS}
  ${LEGAL_ENTITY_FIELDS}
  ${INTEGRATION_FIELDS}
  ${CUSTOM_FIELD_FIELDS}
  ${APPROVAL_THRESHOLD_FIELDS}
  ${INVITATION_FIELDS}
  ${AUDIT_FIELDS}
`

/* ── Mutations ── */

const CREATE_USER = gql`
  mutation AdmCreateUser($input: CreateUserInput!) {
    createUser(input: $input) { success user { ...UserFields } errors { field message } }
  }
  ${USER_FIELDS}
`

const UPDATE_USER_ROLES = gql`
  mutation AdmUpdateUserRoles($userId: ID!, $roles: [Role!]!) {
    updateUserRoles(userId: $userId, roles: $roles) { success user { ...UserFields } errors { field message } }
  }
  ${USER_FIELDS}
`

const DEACTIVATE_USER = gql`
  mutation AdmDeactivateUser($userId: ID!, $reason: String) {
    deactivateUser(userId: $userId, reason: $reason) { success errors { field message } }
  }
`

const ACTIVATE_USER = gql`
  mutation AdmActivateUser($userId: ID!) {
    activateUser(userId: $userId) { success errors { field message } }
  }
`

const UPDATE_CURRENT_USER_PREFERENCES = gql`
  mutation AdmUpdateCurrentUserPreferences($input: UserPreferencesInput!) {
    updateCurrentUserPreferences(input: $input) { success user { ...UserFields } errors { field message } }
  }
  ${USER_FIELDS}
`

const UPDATE_TENANT_SETTINGS = gql`
  mutation AdmUpdateTenantSettings($input: UpdateTenantSettingsInput!) {
    updateTenantSettings(input: $input) { success tenant { ...TenantFields } errors { field message } }
  }
  ${TENANT_FIELDS}
`

const CREATE_LEGAL_ENTITY = gql`
  mutation AdmCreateLegalEntity($input: CreateLegalEntityInput!) {
    createLegalEntity(input: $input) { success legalEntity { ...LegalEntityFields } errors { field message } }
  }
  ${LEGAL_ENTITY_FIELDS}
`

const UPDATE_LEGAL_ENTITY = gql`
  mutation AdmUpdateLegalEntity($legalEntityId: ID!, $input: UpdateLegalEntityInput!) {
    updateLegalEntity(legalEntityId: $legalEntityId, input: $input) {
      success legalEntity { ...LegalEntityFields } errors { field message }
    }
  }
  ${LEGAL_ENTITY_FIELDS}
`

const CREATE_BRANCH = gql`
  mutation AdmCreateBranch($legalEntityId: ID!, $input: CreateBranchInput!) {
    createBranch(legalEntityId: $legalEntityId, input: $input) {
      success branch { id name code } errors { field message }
    }
  }
`

const DELETE_LEGAL_ENTITY = gql`
  mutation AdmDeleteLegalEntity($legalEntityId: ID!) {
    deleteLegalEntity(legalEntityId: $legalEntityId) { success errors { field message } }
  }
`

const CONFIGURE_INTEGRATION = gql`
  mutation AdmConfigureIntegration($input: ConfigureIntegrationInput!) {
    configureIntegration(input: $input) { success integration { ...IntegrationFields } errors { field message } }
  }
  ${INTEGRATION_FIELDS}
`

const TEST_INTEGRATION = gql`
  mutation AdmTestIntegration($integrationId: ID!) {
    testIntegration(integrationId: $integrationId) { success message details }
  }
`

const SYNC_INTEGRATION = gql`
  mutation AdmSyncIntegration($integrationId: ID!, $syncType: String) {
    syncIntegration(integrationId: $integrationId, syncType: $syncType) {
      success recordsProcessed recordsFailed startedAt completedAt
    }
  }
`

const PAUSE_INTEGRATION = gql`
  mutation AdmPauseIntegration($integrationId: ID!) {
    pauseIntegration(integrationId: $integrationId) { success integration { ...IntegrationFields } errors { field message } }
  }
  ${INTEGRATION_FIELDS}
`

const RESUME_INTEGRATION = gql`
  mutation AdmResumeIntegration($integrationId: ID!) {
    resumeIntegration(integrationId: $integrationId) { success integration { ...IntegrationFields } errors { field message } }
  }
  ${INTEGRATION_FIELDS}
`

const CREATE_CUSTOM_FIELD = gql`
  mutation AdmCreateCustomField($input: CreateCustomFieldInput!) {
    createCustomField(input: $input) { success customField { ...CustomFieldFields } errors { field message } }
  }
  ${CUSTOM_FIELD_FIELDS}
`

const UPDATE_CUSTOM_FIELD = gql`
  mutation AdmUpdateCustomField($id: ID!, $input: UpdateCustomFieldInput!) {
    updateCustomField(id: $id, input: $input) { success customField { ...CustomFieldFields } errors { field message } }
  }
  ${CUSTOM_FIELD_FIELDS}
`

const DEACTIVATE_CUSTOM_FIELD = gql`
  mutation AdmDeactivateCustomField($id: ID!) {
    deactivateCustomField(id: $id) { success errors { field message } }
  }
`

const CONFIGURE_APPROVAL_THRESHOLD = gql`
  mutation AdmConfigureApprovalThreshold($input: ConfigureApprovalThresholdInput!) {
    configureApprovalThreshold(input: $input) {
      success approvalThreshold { ...ApprovalThresholdFields } errors { field message }
    }
  }
  ${APPROVAL_THRESHOLD_FIELDS}
`

const DELETE_APPROVAL_THRESHOLD = gql`
  mutation AdmDeleteApprovalThreshold($id: ID!) {
    deleteApprovalThreshold(id: $id) { success errors { field message } }
  }
`

const INVITE_USER = gql`
  mutation AdmInviteUser($input: InviteUserInput!) {
    inviteUser(input: $input) { success invitation { ...InvitationFields } errors { field message } }
  }
  ${INVITATION_FIELDS}
`

const RESEND_INVITATION = gql`
  mutation AdmResendInvitation($id: ID!) {
    resendInvitation(id: $id) { success invitation { ...InvitationFields } errors { field message } }
  }
  ${INVITATION_FIELDS}
`

const REVOKE_INVITATION = gql`
  mutation AdmRevokeInvitation($id: ID!) {
    revokeInvitation(id: $id) { success errors { field message } }
  }
`

/* ── Input shapes ── */

export interface CreateUserInput {
  email: string
  displayName: string
  roles: Role[]
  legalEntityId?: string | null
  azureObjectId?: string
}

export interface AddressInput {
  line1?: string
  city?: string
  state?: string
  country?: string
  postalCode?: string
}

export interface CreateLegalEntityInput {
  name: string
  registrationNumber: string
  address: AddressInput
  defaultCurrency: string
  defaultTaxProfile?: { type: string; taxId?: string | null } | null
}

export interface CreateBranchInput {
  name: string
  code?: string | null
  address: AddressInput
}

export interface TenantSettingsInput {
  multiCurrencyEnabled?: boolean
  defaultCurrency?: string
  timezone?: string
  invoiceNumberingFormat?: string
  enableNotifications?: boolean
  customSettings?: { key: string; value: string }[]
}

export interface ConfigureIntegrationInput {
  type: string
  name: string
  credentials: { key: string; value: string }[]
  syncConfig: {
    syncFrequency: string
    conflictResolutionRule: string
    sourceOfTruth: string
    syncEnabled: boolean
  }
}

export interface UserPreferencesInput {
  language?: string
  dateFormat?: string
  timeFormat?: string
  customPreferences?: { key: string; value: string }[]
}

export interface CreateCustomFieldInput {
  entityType: string
  fieldKey: string
  label: string
  fieldType: string
  required?: boolean
  options?: string[]
}

export interface ConfigureApprovalThresholdInput {
  entityType: string
  minAmount?: number | null
  maxAmount?: number | null
  currency?: string | null
  requiredRole: string
}

export interface InviteUserInput {
  email: string
  roles: Role[]
  legalEntityId?: string | null
}

interface AdminContextValue {
  tenant: Tenant | null
  currentUser: AdminUser | null
  users: AdminUser[]
  legalEntities: LegalEntity[]
  integrations: Integration[]
  customFields: CustomField[]
  approvalThresholds: ApprovalThreshold[]
  invitations: UserInvitation[]
  auditLogs: AuditLog[]
  loading: boolean
  error: string | null
  refetchAll: () => Promise<void>

  createUser: (input: CreateUserInput) => Promise<void>
  updateUserRoles: (userId: string, roles: Role[]) => Promise<void>
  deactivateUser: (userId: string, reason: string) => Promise<void>
  activateUser: (userId: string) => Promise<void>

  updateTenantSettings: (settings: TenantSettingsInput) => Promise<void>
  updateCurrentUserPreferences: (input: UserPreferencesInput) => Promise<void>

  createLegalEntity: (input: CreateLegalEntityInput) => Promise<void>
  updateLegalEntity: (legalEntityId: string, input: Partial<CreateLegalEntityInput>) => Promise<void>
  createBranch: (legalEntityId: string, input: CreateBranchInput) => Promise<void>
  deleteLegalEntity: (legalEntityId: string) => Promise<void>

  configureIntegration: (input: ConfigureIntegrationInput) => Promise<void>
  testIntegration: (integrationId: string) => Promise<{ success: boolean; message: string; details?: string | null }>
  syncIntegration: (integrationId: string) => Promise<void>
  pauseIntegration: (integrationId: string) => Promise<void>
  resumeIntegration: (integrationId: string) => Promise<void>

  createCustomField: (input: CreateCustomFieldInput) => Promise<void>
  updateCustomField: (id: string, input: Partial<CreateCustomFieldInput>) => Promise<void>
  deactivateCustomField: (id: string) => Promise<void>

  configureApprovalThreshold: (input: ConfigureApprovalThresholdInput) => Promise<void>
  deleteApprovalThreshold: (id: string) => Promise<void>

  inviteUser: (input: InviteUserInput) => Promise<void>
  resendInvitation: (id: string) => Promise<void>
  revokeInvitation: (id: string) => Promise<void>
}

const AdminContext = createContext<AdminContextValue | null>(null)

export function AdminProvider({ children }: { children: ReactNode }) {
  const { user, setResolvedRoles } = useAuth()
  const hasAdminAccess = canAccessModule(user?.roles ?? [], 'admin')
  // currentUser is needed by every user (Profile/preferences, role display).
  const currentUserQ = useQuery(GET_CURRENT_USER, { errorPolicy: 'all' })
  // The admin overview is admin/auditor-only on the backend — skip it otherwise.
  const overviewQ = useQuery(GET_ADMIN, { errorPolicy: 'all', skip: !hasAdminAccess })

  const [createUserMut] = useMutation(CREATE_USER)
  const [updateUserRolesMut] = useMutation(UPDATE_USER_ROLES)
  const [deactivateUserMut] = useMutation(DEACTIVATE_USER)
  const [activateUserMut] = useMutation(ACTIVATE_USER)
  const [updateTenantSettingsMut] = useMutation(UPDATE_TENANT_SETTINGS)
  const [updateCurrentUserPreferencesMut] = useMutation(UPDATE_CURRENT_USER_PREFERENCES)
  const [createLegalEntityMut] = useMutation(CREATE_LEGAL_ENTITY)
  const [updateLegalEntityMut] = useMutation(UPDATE_LEGAL_ENTITY)
  const [createBranchMut] = useMutation(CREATE_BRANCH)
  const [deleteLegalEntityMut] = useMutation(DELETE_LEGAL_ENTITY)
  const [configureIntegrationMut] = useMutation(CONFIGURE_INTEGRATION)
  const [testIntegrationMut] = useMutation(TEST_INTEGRATION)
  const [syncIntegrationMut] = useMutation(SYNC_INTEGRATION)
  const [pauseIntegrationMut] = useMutation(PAUSE_INTEGRATION)
  const [resumeIntegrationMut] = useMutation(RESUME_INTEGRATION)
  const [createCustomFieldMut] = useMutation(CREATE_CUSTOM_FIELD)
  const [updateCustomFieldMut] = useMutation(UPDATE_CUSTOM_FIELD)
  const [deactivateCustomFieldMut] = useMutation(DEACTIVATE_CUSTOM_FIELD)
  const [configureApprovalThresholdMut] = useMutation(CONFIGURE_APPROVAL_THRESHOLD)
  const [deleteApprovalThresholdMut] = useMutation(DELETE_APPROVAL_THRESHOLD)
  const [inviteUserMut] = useMutation(INVITE_USER)
  const [resendInvitationMut] = useMutation(RESEND_INVITATION)
  const [revokeInvitationMut] = useMutation(REVOKE_INVITATION)

  const data = overviewQ.data
  const tenant: Tenant | null = data?.currentTenant ?? null
  const currentUser: AdminUser | null = currentUserQ.data?.currentUser ?? null

  // The id token B2C hands the SPA does not reliably carry the enriched `roles`
  // claim, so promote the authoritative backend roles (adm_user_roles) into the
  // auth context; all UI gating then reflects the user's real RBAC assignment.
  const resolvedRolesKey = (currentUser?.roles ?? []).join(',')
  useEffect(() => {
    if (currentUser?.roles) setResolvedRoles(normalizeRoles(currentUser.roles))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedRolesKey])
  const users: AdminUser[] = useMemo(() => data?.users?.users ?? [], [data])
  const legalEntities: LegalEntity[] = useMemo(() => data?.legalEntities ?? [], [data])
  const integrations: Integration[] = useMemo(() => data?.integrations ?? [], [data])
  const customFields: CustomField[] = useMemo(() => data?.customFields ?? [], [data])
  const approvalThresholds: ApprovalThreshold[] = useMemo(() => data?.approvalThresholds ?? [], [data])
  const invitations: UserInvitation[] = useMemo(() => data?.invitations ?? [], [data])
  const auditLogs: AuditLog[] = useMemo(() => data?.auditLogs?.auditLogs ?? [], [data])

  const refetchAll = useCallback(async () => {
    await currentUserQ.refetch()
    if (hasAdminAccess) await overviewQ.refetch()
  }, [currentUserQ, overviewQ, hasAdminAccess])

  const createUser = useCallback<AdminContextValue['createUser']>(
    async (input) => {
      await createUserMut({
        variables: {
          input: {
            email: input.email,
            displayName: input.displayName,
            roles: input.roles,
            legalEntityId: input.legalEntityId ?? null,
            azureObjectId: input.azureObjectId ?? `local-${crypto.randomUUID()}`,
          },
        },
      })
      await refetchAll()
    },
    [createUserMut, refetchAll],
  )

  const updateUserRoles = useCallback<AdminContextValue['updateUserRoles']>(
    async (userId, roles) => {
      await updateUserRolesMut({ variables: { userId, roles } })
      await refetchAll()
    },
    [updateUserRolesMut, refetchAll],
  )

  const deactivateUser = useCallback<AdminContextValue['deactivateUser']>(
    async (userId, reason) => {
      await deactivateUserMut({ variables: { userId, reason } })
      await refetchAll()
    },
    [deactivateUserMut, refetchAll],
  )

  const activateUser = useCallback<AdminContextValue['activateUser']>(
    async (userId) => {
      await activateUserMut({ variables: { userId } })
      await refetchAll()
    },
    [activateUserMut, refetchAll],
  )

  const updateTenantSettings = useCallback<AdminContextValue['updateTenantSettings']>(
    async (settings) => {
      await updateTenantSettingsMut({ variables: { input: { settings } } })
      await refetchAll()
    },
    [updateTenantSettingsMut, refetchAll],
  )

  const updateCurrentUserPreferences = useCallback<
    AdminContextValue['updateCurrentUserPreferences']
  >(
    async (input) => {
      await updateCurrentUserPreferencesMut({ variables: { input } })
      await refetchAll()
    },
    [updateCurrentUserPreferencesMut, refetchAll],
  )

  const createLegalEntity = useCallback<AdminContextValue['createLegalEntity']>(
    async (input) => {
      await createLegalEntityMut({ variables: { input } })
      await refetchAll()
    },
    [createLegalEntityMut, refetchAll],
  )

  const updateLegalEntity = useCallback<AdminContextValue['updateLegalEntity']>(
    async (legalEntityId, input) => {
      await updateLegalEntityMut({ variables: { legalEntityId, input } })
      await refetchAll()
    },
    [updateLegalEntityMut, refetchAll],
  )

  const createBranch = useCallback<AdminContextValue['createBranch']>(
    async (legalEntityId, input) => {
      await createBranchMut({ variables: { legalEntityId, input } })
      await refetchAll()
    },
    [createBranchMut, refetchAll],
  )

  const deleteLegalEntity = useCallback<AdminContextValue['deleteLegalEntity']>(
    async (legalEntityId) => {
      await deleteLegalEntityMut({ variables: { legalEntityId } })
      await refetchAll()
    },
    [deleteLegalEntityMut, refetchAll],
  )

  const configureIntegration = useCallback<AdminContextValue['configureIntegration']>(
    async (input) => {
      await configureIntegrationMut({ variables: { input } })
      await refetchAll()
    },
    [configureIntegrationMut, refetchAll],
  )

  const testIntegration = useCallback<AdminContextValue['testIntegration']>(
    async (integrationId) => {
      const { data: res } = await testIntegrationMut({ variables: { integrationId } })
      await refetchAll()
      const r = res?.testIntegration
      return { success: !!r?.success, message: r?.message ?? '', details: r?.details }
    },
    [testIntegrationMut, refetchAll],
  )

  const syncIntegration = useCallback<AdminContextValue['syncIntegration']>(
    async (integrationId) => {
      await syncIntegrationMut({ variables: { integrationId, syncType: 'FULL' } })
      await refetchAll()
    },
    [syncIntegrationMut, refetchAll],
  )

  const pauseIntegration = useCallback<AdminContextValue['pauseIntegration']>(
    async (integrationId) => {
      await pauseIntegrationMut({ variables: { integrationId } })
      await refetchAll()
    },
    [pauseIntegrationMut, refetchAll],
  )

  const resumeIntegration = useCallback<AdminContextValue['resumeIntegration']>(
    async (integrationId) => {
      await resumeIntegrationMut({ variables: { integrationId } })
      await refetchAll()
    },
    [resumeIntegrationMut, refetchAll],
  )

  const createCustomField = useCallback<AdminContextValue['createCustomField']>(
    async (input) => {
      await createCustomFieldMut({ variables: { input } })
      await refetchAll()
    },
    [createCustomFieldMut, refetchAll],
  )

  const updateCustomField = useCallback<AdminContextValue['updateCustomField']>(
    async (id, input) => {
      await updateCustomFieldMut({ variables: { id, input } })
      await refetchAll()
    },
    [updateCustomFieldMut, refetchAll],
  )

  const deactivateCustomField = useCallback<AdminContextValue['deactivateCustomField']>(
    async (id) => {
      await deactivateCustomFieldMut({ variables: { id } })
      await refetchAll()
    },
    [deactivateCustomFieldMut, refetchAll],
  )

  const configureApprovalThreshold = useCallback<AdminContextValue['configureApprovalThreshold']>(
    async (input) => {
      await configureApprovalThresholdMut({ variables: { input } })
      await refetchAll()
    },
    [configureApprovalThresholdMut, refetchAll],
  )

  const deleteApprovalThreshold = useCallback<AdminContextValue['deleteApprovalThreshold']>(
    async (id) => {
      await deleteApprovalThresholdMut({ variables: { id } })
      await refetchAll()
    },
    [deleteApprovalThresholdMut, refetchAll],
  )

  const inviteUser = useCallback<AdminContextValue['inviteUser']>(
    async (input) => {
      await inviteUserMut({
        variables: {
          input: {
            email: input.email,
            roles: input.roles,
            legalEntityId: input.legalEntityId ?? null,
          },
        },
      })
      await refetchAll()
    },
    [inviteUserMut, refetchAll],
  )

  const resendInvitation = useCallback<AdminContextValue['resendInvitation']>(
    async (id) => {
      await resendInvitationMut({ variables: { id } })
      await refetchAll()
    },
    [resendInvitationMut, refetchAll],
  )

  const revokeInvitation = useCallback<AdminContextValue['revokeInvitation']>(
    async (id) => {
      await revokeInvitationMut({ variables: { id } })
      await refetchAll()
    },
    [revokeInvitationMut, refetchAll],
  )

  const value = useMemo<AdminContextValue>(
    () => ({
      tenant,
      currentUser,
      users,
      legalEntities,
      integrations,
      customFields,
      approvalThresholds,
      invitations,
      auditLogs,
      loading: overviewQ.loading || currentUserQ.loading,
      error: overviewQ.error ? overviewQ.error.message : null,
      refetchAll,
      createUser,
      updateUserRoles,
      deactivateUser,
      activateUser,
      updateTenantSettings,
      updateCurrentUserPreferences,
      createLegalEntity,
      updateLegalEntity,
      createBranch,
      deleteLegalEntity,
      configureIntegration,
      testIntegration,
      syncIntegration,
      pauseIntegration,
      resumeIntegration,
      createCustomField,
      updateCustomField,
      deactivateCustomField,
      configureApprovalThreshold,
      deleteApprovalThreshold,
      inviteUser,
      resendInvitation,
      revokeInvitation,
    }),
    [
      tenant, currentUser, users, legalEntities, integrations, customFields, approvalThresholds, invitations, auditLogs,
      overviewQ.loading, overviewQ.error, currentUserQ.loading, refetchAll,
      createUser, updateUserRoles, deactivateUser, activateUser, updateTenantSettings,
      updateCurrentUserPreferences,
      createLegalEntity, updateLegalEntity, createBranch, deleteLegalEntity,
      configureIntegration, testIntegration, syncIntegration, pauseIntegration, resumeIntegration,
      createCustomField, updateCustomField, deactivateCustomField,
      configureApprovalThreshold, deleteApprovalThreshold,
      inviteUser, resendInvitation, revokeInvitation,
    ],
  )

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
}

export function useAdmin(): AdminContextValue {
  const ctx = useContext(AdminContext)
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider')
  return ctx
}
